import { SingleUnitGetTargetQuery$data } from '../graphQLOperations/__generated__/SingleUnitGetTargetQuery.graphql';
import { SingleUnitGetTargetWithLessonsQuery$data } from '../graphQLOperations/__generated__/SingleUnitGetTargetWithLessonsQuery.graphql';
import { CountPerTypes, SkillsByType } from '../models/SingleUnit';
import { Skill } from '../models/Skill';
import { SkillAncestriesId } from '../models/SkillAncestries';
import { TargetSkillTypeLabel } from '../models/TargetSkillType';
import { SkillsCurriculumLessons } from '../pages/SingleUnit';
import { createAdditionalSupportSkills } from './createAdditionalSupportSkills';
import { sortSkillsByDifficulty } from './sortCriteria';

/**
 * Enriches a skill object with its targetSkillType.
 * Single source of truth for skill type enrichment.
 * This is the ONLY place where targetSkillType should be added to a skill.
 */
export const enrichSkillWithType = (skill: Skill, targetSkillType: TargetSkillTypeLabel): Skill => ({
  ...skill,
  targetSkillType,
});

export type TargetSkillNode = {
  target:
    | {
        id: string | null | undefined;
        isActive: boolean;
        name: string | null | undefined;
        summary: string | null | undefined;
        targetGroup:
          | {
              name: string | null | undefined;
            }
          | null
          | undefined;
        targetSkills:
          | Array<
              | {
                  id: string;
                  isActive: boolean;
                  skill:
                    | {
                        code: string | null | undefined;
                        difficulty: string;
                        id: string;
                        name: string | null | undefined;
                        lessonNumber?: string[];
                        standards?: ReadonlyArray<
                          { readonly id: string; readonly shortName: string | null | undefined } | null | undefined
                        > | null;
                        skillAncestries:
                          | Array<
                              | {
                                  ancestorSkill:
                                    | {
                                        code: string | null | undefined;
                                        difficulty: string;
                                        id: string;
                                        name: string | null | undefined;
                                        lessonNumber?: string[];
                                        standards?: ReadonlyArray<
                                          | { readonly id: string; readonly shortName: string | null | undefined }
                                          | null
                                          | undefined
                                        > | null;
                                      }
                                    | null
                                    | undefined;
                                  id: string;
                                  skillRelevance:
                                    | {
                                        id: string;
                                        name: string | null | undefined;
                                      }
                                    | null
                                    | undefined;
                                }
                              | null
                              | undefined
                            >
                          | null
                          | undefined;
                      }
                    | null
                    | undefined;
                  targetSkillType:
                    | {
                        id: string;
                        name: string | null | undefined;
                      }
                    | null
                    | undefined;
                }
              | null
              | undefined
            >
          | null
          | undefined;
      }
    | null
    | undefined;
};
export const getHighAndMediumRelevanceAncestries = (skillsTypeTarget: Skill[]): Skill[] => {
  return Array.from(
    new Map(
      skillsTypeTarget.flatMap(
        el =>
          el.skillAncestries
            ?.filter(e => e.skillRelevance.id !== SkillAncestriesId.LOW)
            .map(e => [e.ancestorSkill.id, enrichSkillWithType(e.ancestorSkill, TargetSkillTypeLabel.SUPPORT)]) ?? [],
      ),
    ).values(),
  );
};

export const filterOrAddSkillsByLessons = (
  selectedLessons: string[] | undefined,
  allSkillsByUnitWithLessons: TargetSkillNode | SingleUnitGetTargetQuery$data,
  skillCurriculumLessons: SkillsCurriculumLessons[],
) => {
  let matchedLessons: SkillsCurriculumLessons[] = [];
  if (selectedLessons && selectedLessons.length > 0) {
    matchedLessons = skillCurriculumLessons.filter(l => selectedLessons.includes(l.id));
  }

  //define which set of skill curriculum lessons are the target depending if there is a filter applied
  const skillLessonFilterOrAdd =
    selectedLessons && selectedLessons.length > 0 ? matchedLessons : skillCurriculumLessons;

  const skillLessonMap = skillLessonFilterOrAdd.reduce<Record<string, string[]>>((acc, lesson) => {
    for (const skill of lesson.skills) {
      if (!acc[skill.id]) acc[skill.id] = [];
      acc[skill.id].push(lesson.number);
    }
    return acc;
  }, {});

  Object.keys(skillLessonMap).forEach(key => {
    skillLessonMap[key].sort((a, b) => +a - +b);
  });

  //same criteria that above, filter the allSkillsByUnitWithLessons or leave it as is
  const filterOrMapTargetSkills =
    selectedLessons && selectedLessons.length > 0
      ? allSkillsByUnitWithLessons?.target?.targetSkills?.filter(ts => ts?.skill && skillLessonMap[ts.skill.id])
      : allSkillsByUnitWithLessons?.target?.targetSkills;

  const targetSkillsNode = filterOrMapTargetSkills?.map(ts => {
    const lessonNumbers = ts?.skill && ts?.skill.id ? skillLessonMap[ts.skill.id] : [];
    const enrichedAncestries = ts?.skill?.skillAncestries?.map(sa => {
      const ancestorId = sa?.ancestorSkill?.id;
      const ancestorLessons = ancestorId ? skillLessonMap[ancestorId] ?? [] : [];

      return {
        ...sa,
        ancestorSkill: {
          ...sa?.ancestorSkill,
          ...(ancestorLessons.length > 0 ? { lessonNumbers: ancestorLessons } : {}),
        },
      };
    });

    return {
      ...ts,
      skill: {
        ...ts?.skill,
        ...(lessonNumbers && lessonNumbers.length > 0 ? { lessonNumbers } : {}),
        skillAncestries: enrichedAncestries,
      },
    };
  });

  return {
    ...allSkillsByUnitWithLessons,
    target: {
      ...allSkillsByUnitWithLessons.target,
      targetSkills: targetSkillsNode,
    },
  };
};

export const singleUnitHelper = (
  data: SingleUnitGetTargetQuery$data | SingleUnitGetTargetWithLessonsQuery$data | TargetSkillNode,
) => {
  const filterSkillsByTargetSkillType = (type: TargetSkillTypeLabel): Skill[] => {
    return data
      ? ((data?.target?.targetSkills?.filter(el => el?.targetSkillType?.name === type).map(element => element?.skill) ||
          []) as unknown as Skill[])
      : [];
  };

  const tsIds = data?.target?.targetSkills?.map(el => {
    return el?.skill?.id;
  }) as string[];

  const skillsTypeTarget = filterSkillsByTargetSkillType(TargetSkillTypeLabel.TARGET).map(skill =>
    enrichSkillWithType(skill, TargetSkillTypeLabel.TARGET),
  );
  const skillsTypeSupport = filterSkillsByTargetSkillType(TargetSkillTypeLabel.SUPPORT).map(skill =>
    enrichSkillWithType(skill, TargetSkillTypeLabel.SUPPORT),
  );
  const skillsTypeExtension = filterSkillsByTargetSkillType(TargetSkillTypeLabel.EXTENSION).map(skill =>
    enrichSkillWithType(skill, TargetSkillTypeLabel.EXTENSION),
  );

  const highMediumRelevanceAncestries = getHighAndMediumRelevanceAncestries(skillsTypeTarget);
  const additionalSkillsTypeSupport = createAdditionalSupportSkills(skillsTypeSupport, highMediumRelevanceAncestries);

  const dataSetForType: SkillsByType[] = [
    {
      skills: skillsTypeTarget.sort(sortSkillsByDifficulty),
      targetSkillType: TargetSkillTypeLabel.TARGET,
    },
    {
      skills: additionalSkillsTypeSupport.sort(sortSkillsByDifficulty),
      targetSkillType: TargetSkillTypeLabel.SUPPORT,
    },
    {
      skills: skillsTypeExtension.sort(sortSkillsByDifficulty),
      targetSkillType: TargetSkillTypeLabel.EXTENSION,
    },
  ];

  const countPerTypes: CountPerTypes = {
    targetName: data.target?.summary as string,
    types: [
      {
        targetSkillType: TargetSkillTypeLabel.TARGET,
        count: skillsTypeTarget?.length as number,
      },
      {
        targetSkillType: TargetSkillTypeLabel.SUPPORT,
        count: highMediumRelevanceAncestries.filter(
          skill => !skillsTypeTarget.map(el => el.id).includes(skill.id) && tsIds.includes(skill.id),
        ).length as number,
      },
      {
        targetSkillType: TargetSkillTypeLabel.EXTENSION,
        count: skillsTypeExtension?.length as number,
      },
    ],
  };

  return { countPerTypes, dataSetForType, tsIds };
};
